import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { UserRole } from '@/types/database/users';
import { subMonths, isAfter, isBefore, startOfMonth, endOfMonth } from 'date-fns';

interface RoleDashboardStats {
  [key: string]: any;
  isLoading: boolean;
  error: Error | null;
}

export const useRoleDashboardStats = (role: UserRole | null): RoleDashboardStats => {
  const [stats, setStats] = useState<RoleDashboardStats>({
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    if (!role) return;

    const fetchRoleSpecificStats = async () => {
      try {
        const today = new Date();
        const currentMonthStart = startOfMonth(today);
        const lastMonthStart = startOfMonth(subMonths(today, 1));
        
        let roleStats = {};

        switch (role) {
          case 'admin':
            roleStats = await fetchAdminStats(currentMonthStart, lastMonthStart);
            break;
          case 'finance':
            roleStats = await fetchFinanceStats(currentMonthStart, lastMonthStart);
            break;
          case 'warehouse':
            roleStats = await fetchWarehouseStats(currentMonthStart, lastMonthStart);
            break;
          case 'seller':
            roleStats = await fetchSellerStats(currentMonthStart, lastMonthStart);
            break;
          case 'support':
            roleStats = await fetchSupportStats();
            break;
          case 'benchmark':
            roleStats = await fetchBenchmarkStats(currentMonthStart, lastMonthStart);
            break;
          case 'benchmark_manager':
            roleStats = await fetchBenchmarkManagerStats(currentMonthStart, lastMonthStart);
            break;
          default:
            roleStats = await fetchDefaultStats();
        }

        setStats({
          ...roleStats,
          isLoading: false,
          error: null,
        });
      } catch (error) {
        console.error('Error fetching role-specific stats:', error);
        setStats(prev => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error : new Error('Unknown error'),
        }));
      }
    };

    fetchRoleSpecificStats();
  }, [role]);

  return stats;
};

const fetchAdminStats = async (currentMonthStart: Date, lastMonthStart: Date) => {
  // Fetch comprehensive admin stats using count for better performance
  const [procurements, harvestsCount, farmsCount, alerts, usersCount] = await Promise.all([
    supabase.from('procurements').select('*'),
    supabase.from('harvests').select('*', { count: 'exact', head: false }),
    supabase.from('farms').select('*', { count: 'exact', head: false }),
    supabase.from('alerts').select('*'),
    supabase.from('user_profiles').select('*', { count: 'exact', head: false })
  ]);

  const totalValue = procurements.data?.reduce((sum, p) => sum + (p.total_amount || 0), 0) || 0;
  const pendingApprovals = procurements.data?.filter(p => p.status === 'pending_finance').length || 0;
  const activeHarvests = harvestsCount.data?.filter(h => ['planned', 'in_progress'].includes(h.status)).length || 0;
  const totalFarms = farmsCount.count || 0;
  const unreadAlerts = alerts.data?.filter(a => !a.is_read).length || 0;
  const totalUsers = usersCount.count || 0;

  return {
    totalValue,
    pendingApprovals,
    activeHarvests,
    totalFarms,
    unreadAlerts,
    totalUsers,
    recentProcurements: procurements.data?.slice(0, 5) || [],
    criticalAlerts: alerts.data?.filter(a => !a.is_read && a.type === 'critical').slice(0, 3) || []
  };
};

const fetchFinanceStats = async (currentMonthStart: Date, lastMonthStart: Date) => {
  const [procurements, financeData] = await Promise.all([
    supabase.from('procurements').select('*, procurement_finance(*)'),
    supabase.from('procurement_finance').select('*')
  ]);

  const pendingApprovals = procurements.data?.filter(p => p.status === 'pending_finance').length || 0;
  const approvedThisMonth = procurements.data?.filter(p => 
    p.status === 'pending_warehouse' && 
    p.procurement_finance?.[0]?.finance_approved_at &&
    isAfter(new Date(p.procurement_finance[0].finance_approved_at), currentMonthStart)
  ).length || 0;
  
  const totalValue = procurements.data?.reduce((sum, p) => sum + (p.total_amount || 0), 0) || 0;
  const monthlyValue = procurements.data?.filter(p => 
    isAfter(new Date(p.created_at), currentMonthStart)
  ).reduce((sum, p) => sum + (p.total_amount || 0), 0) || 0;

  return {
    pendingApprovals,
    approvedThisMonth,
    totalValue,
    monthlyValue,
    pendingProcurements: procurements.data?.filter(p => p.status === 'pending_finance').slice(0, 10) || [],
    recentApprovals: procurements.data?.filter(p => p.status === 'pending_warehouse').slice(0, 5) || []
  };
};

const fetchWarehouseStats = async (currentMonthStart: Date, lastMonthStart: Date) => {
  const [procurements, warehouseData] = await Promise.all([
    supabase.from('procurements').select('*, procurement_warehouse(*)'),
    supabase.from('procurement_warehouse').select('*')
  ]);

  const pendingInspections = procurements.data?.filter(p => p.status === 'pending_warehouse').length || 0;
  const completedThisMonth = procurements.data?.filter(p => 
    p.status === 'completed' && 
    p.procurement_warehouse?.[0]?.warehouse_confirmation_date &&
    isAfter(new Date(p.procurement_warehouse[0].warehouse_confirmation_date), currentMonthStart)
  ).length || 0;

  const totalQuantity = procurements.data?.reduce((sum, p) => sum + (p.total_quantity || 0), 0) || 0;
  const rejectedItems = warehouseData.data?.filter(w => w.warehouse_rejected_by).length || 0;

  return {
    pendingInspections,
    completedThisMonth,
    totalQuantity,
    rejectedItems,
    pendingProcurements: procurements.data?.filter(p => p.status === 'pending_warehouse').slice(0, 10) || [],
    recentCompletions: procurements.data?.filter(p => p.status === 'completed').slice(0, 5) || []
  };
};

const fetchSellerStats = async (currentMonthStart: Date, lastMonthStart: Date) => {
  const today = new Date();
  
  const [procurements, farms, harvests, suppliers, agents] = await Promise.all([
    supabase.from('procurements').select('*'),
    supabase.from('farms').select('*'),
    supabase.from('harvests').select('*'),
    supabase.from('suppliers').select('*'),
    supabase.from('agents').select('*')
  ]);

  const activeProcurements = procurements.data?.filter(p => 
    ['initiated', 'pending_finance', 'pending_warehouse'].includes(p.status)
  ).length || 0;
  
  const monthlyProcurements = procurements.data?.filter(p => 
    isAfter(new Date(p.created_at), currentMonthStart)
  ).length || 0;

  const upcomingHarvests = harvests.data?.filter(h => 
    h.status === 'planned' && h.expected_date && 
    isAfter(new Date(h.expected_date), today) &&
    isBefore(new Date(h.expected_date), new Date(Date.now() + 30 * 24 * 60 * 60 * 1000))
  ).length || 0;

  const totalSuppliers = suppliers.data?.filter(s => s.status === 'active').length || 0;
  const totalAgents = agents.data?.filter(a => a.status === 'active').length || 0;

  return {
    activeProcurements,
    monthlyProcurements,
    upcomingHarvests,
    totalSuppliers,
    totalAgents,
    recentProcurements: procurements.data?.slice(0, 5) || [],
    upcomingHarvestsList: harvests.data?.filter(h => h.status === 'planned').slice(0, 5) || []
  };
};

const fetchBenchmarkStats = async (currentMonthStart: Date, lastMonthStart: Date) => {
  // Fetch price data from all benchmark tables
  const [farmPrices, distributionPrices, localShopPrices, sundayMarketPrices, supermarketPrices, ecommercePrices] = await Promise.all([
    supabase.from('farm_prices').select('*').order('created_at', { ascending: false }),
    supabase.from('distribution_center_prices').select('*').order('created_at', { ascending: false }),
    supabase.from('local_shop_prices').select('*').order('created_at', { ascending: false }),
    supabase.from('sunday_market_prices').select('*').order('created_at', { ascending: false }),
    supabase.from('supermarket_prices').select('*').order('created_at', { ascending: false }),
    supabase.from('ecommerce_prices').select('*').order('created_at', { ascending: false })
  ]);

  // Calculate total entries
  const totalEntries = [
    farmPrices.data?.length || 0,
    distributionPrices.data?.length || 0,
    localShopPrices.data?.length || 0,
    sundayMarketPrices.data?.length || 0,
    supermarketPrices.data?.length || 0,
    ecommercePrices.data?.length || 0
  ].reduce((sum, count) => sum + count, 0);

  // Calculate today's entries
  const today = new Date().toISOString().split('T')[0];
  const todayEntries = [
    farmPrices.data?.filter(p => p.date === today).length || 0,
    distributionPrices.data?.filter(p => p.date === today).length || 0,
    localShopPrices.data?.filter(p => p.date === today).length || 0,
    sundayMarketPrices.data?.filter(p => p.date === today).length || 0,
    supermarketPrices.data?.filter(p => p.date === today).length || 0,
    ecommercePrices.data?.filter(p => p.date === today).length || 0
  ].reduce((sum, count) => sum + count, 0);

  // Count markets with data
  const marketsWithData = [
    (farmPrices.data?.length || 0) > 0,
    (distributionPrices.data?.length || 0) > 0,
    (localShopPrices.data?.length || 0) > 0,
    (sundayMarketPrices.data?.length || 0) > 0,
    (supermarketPrices.data?.length || 0) > 0,
    (ecommercePrices.data?.length || 0) > 0
  ].filter(Boolean).length;

  // Combine all recent entries and sort by created_at
  const allEntries = [
    ...(farmPrices.data?.slice(0, 5).map(entry => ({ ...entry, source_type: 'Farm Price' })) || []),
    ...(distributionPrices.data?.slice(0, 5).map(entry => ({ ...entry, source_type: 'Distribution Center' })) || []),
    ...(localShopPrices.data?.slice(0, 5).map(entry => ({ ...entry, source_type: 'Local Shop' })) || []),
    ...(sundayMarketPrices.data?.slice(0, 5).map(entry => ({ ...entry, source_type: 'Sunday Market' })) || []),
    ...(supermarketPrices.data?.slice(0, 5).map(entry => ({ ...entry, source_type: 'Supermarket' })) || []),
    ...(ecommercePrices.data?.slice(0, 5).map(entry => ({ ...entry, source_type: 'E-commerce' })) || [])
  ];

  // Sort by created_at and take the 10 most recent
  const recentEntries = allEntries
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 10);

  return {
    totalEntries,
    todayEntries,
    marketsTracked: marketsWithData,
    monthlyEntries: 0, // Could be calculated if needed
    recentEntries
  };
};

const fetchSupportStats = async () => {
  const [alerts, procurements] = await Promise.all([
    supabase.from('alerts').select('*'),
    supabase.from('procurements').select('*')
  ]);

  const openIssues = alerts.data?.filter(a => !a.action_taken).length || 0;
  const criticalIssues = alerts.data?.filter(a => a.type === 'critical' && !a.action_taken).length || 0;
  const resolvedToday = alerts.data?.filter(a => 
    a.action_taken && 
    isAfter(new Date(a.created_at), new Date(Date.now() - 24 * 60 * 60 * 1000))
  ).length || 0;

  const problemProcurements = procurements.data?.filter(p => p.status === 'rejected').length || 0;

  return {
    openIssues,
    criticalIssues,
    resolvedToday,
    problemProcurements,
    pendingIssues: alerts.data?.filter(a => !a.action_taken).slice(0, 10) || [],
    recentResolutions: alerts.data?.filter(a => a.action_taken).slice(0, 5) || []
  };
};

const fetchBenchmarkManagerStats = async (currentMonthStart: Date, lastMonthStart: Date) => {
  // Benchmark managers get the same data as benchmark users but with additional management context
  const benchmarkStats = await fetchBenchmarkStats(currentMonthStart, lastMonthStart);
  
  // Add management-specific stats
  return {
    ...benchmarkStats,
    managementView: true, // Flag to indicate this is a management view
  };
};

const fetchDefaultStats = async () => {
  return {
    message: 'Welcome to ChipChip ERP',
    hasAccess: false
  };
};
